/* eslint-disable no-underscore-dangle */
import axios from 'axios';
import _ from 'underscore';
import $ from '../../jquery';
import namespace from '../namespace';
import OfcsObject from '../object';

// I'm not sure why this is done. Probably to prevent bootstraps internal focus handling.
$.fn.modal.Constructor.prototype.enforceFocus = function enforceFocus() {};

/**
 * Modal prototype
 *
 *     var modal = new Modal({
 *          url: "/some/path.html",
 *          loaderHtml: '<p>Loading</p>',
 *          header: "This is some header template <%= var %>",
 *          body: "This is some body template <%= var %>",
 *          buttons: [
 *              {label: "Button1", type: "primary", event: "confirm dismiss"},
 *              {label: "Button2", type: "danger", icon: "trash"}
 *          ],
 *          parent: parentElement,
 *          selectors: {
 *              header: ".modal-header h3",
 *              body: ".modal-body",
 *              buttons: ".modal-footer"
 *          }
 *          template: [
 *              '<div class="modal">',
 *                  '<div class="modal-header">',
 *                      '<h3></h3>',
 *                  '</div>',
 *                  '<div class="modal-body"></div>',
 *                  '<div class="modal-footer"></div>',
 *              '</div>'
 *          ].join(""),
 *          keyboard: true,
 *          backdrop: true,
 *          on: {
 *              confirm: function(event) {
 *                  // code here
 *              }
 *          }
 *     });
 */
class Modal extends OfcsObject {
    /**
     * @property {Object} options
     * Options for the current modal instance
     */

    /**
     * @property {jQuery} modalElement
     * Modal HTML element
     */

    /**
     * @property {jQuery} modalContainer
     * Container of modalElement which gets hooked up to bootstraps modal
     */

    /**
     * @property {jQuery} modalContainer
     * Container that is placed inside modalContainer and receives focus
     */

    /**
     * @event beforeShow
     * Fired just before the modal is injected into its parent element
     */

    /**
     * @event shown
     * Fired just after the modal is injected into its parent element and displayed
     */

    /**
     * @event beforeHide
     * Fired just before the modal is removed from DOM
     */

    /**
     * Fired after the modal was removed from DOM
     *
     * @event Ofcs.Modal.Modal#hidden
     * @type {Object}
     */

    /**
     * @property defaultOptions
     * Default options for new modals
     */
    defaultOptions = {
        /**
         * URL used for ajax request which loads the whole content of the modal
         * @cfg {String} url
         */
        url: null,

        /**
         * Defines the template for the loader. Use the <%= message => variable within the template to define
         * the position of the actual loading message
         * @cfg {String} loaderTemplate
         */
        loaderTemplate: '<p><%=message %><% if (percent) { %> (<%=percent%>%)<%}%></p>',

        /**
         * A list of key:value pairs that can be used with showLoaderByMessageKey() to show the specified message.
         * Special keys that are used within the modal that can be overwritten are: _default and _saving
         * @cfg {Object} loaderMessages
         */
        loaderMessages: {
            _default: 'Loading',
            _saving: 'Saving',
        },

        /**
         * String to display as headline in modal. Can contain Underscore.js template code.
         * @cfg {String} header
         */
        header: '',

        /**
         * HTML to display as body of headline. Can contain Underscore.js template code.
         * @cfg {String} body
         */
        body: '',

        /**
         * Closes the modal when escape key is pressed
         * @cfg {Boolean} [keyboard=true]
         */
        keyboard: true,

        /**
         * Includes a modal-backdrop element. Alternatively, specify static for a backdrop
         * which doesn't close the modal on click.
         * @cfg {Boolean} [backdrop='static']
         */
        backdrop: 'static',

        /**
         * Array of button definitions
         * @cfg {Object[]} buttons
         * @cfg {String} buttons.label Label of Button
         * @cfg {String} [buttons.type] Type of button (primary, danger ...)
         * @cfg {String} [buttons.event] Event to be triggered on click
         * @cfg {String} [buttons.icon] Icon to be added in front of the label
         */
        buttons: [
            { label: 'OK', event: 'dismiss' },
        ],

        /**
         * Selector or jQuery Element to inject the modal into
         * @cfg {String|jQuery} [parent='body']
         */
        parent: 'body',

        /**
         * Selectors of the elements within the template option where to inject messages and buttons
         * @cfg {Object} selectors
         * @cfg {String} [selectors.header=".modal-header h3"] Selector of the parent element that should receive the
         *                                                     header text
         * @cfg {String} [selectors.body=".modal-body"] Selector of the parent element that should receive the body text
         * @cfg {String} [selectors.buttons=".modal-footer"] Selector of the parent element that should receive the
         *                                                   buttons
         */
        selectors: {
            header: '.modal-header h3',
            body: '.modal-body',
            buttons: '.modal-footer',
        },

        /**
         * HTML template for the modal
         * @cfg {String} [template]
         */
        template: `
            <div class="modal">
                <div class="modal-header">
                    <h3></h3>
                </div>
                <div class="modal-body"></div>
                <div class="modal-footer"></div>
            </div>
        `,

        /**
         * Add custom event listeners, see example code
         * @cfg {Object} [on]
         */
        on: {},

        captureKeyboard: false,
    };

    /**
     *
     */
    defaultEvents = {
        /**
         * @event dismiss
         * Triggering this event on the modal will hide the modal
         */
        dismiss() {
            this.hide();
        },

        /**
         * @event submit
         * Triggering this event on the modal will submit the first found form via ajax and use the response as new
         * content for the modal
         */
        submit() {
            // eslint-disable-next-line no-console
            this.submit().catch(console.error);
        },

        /**
         * @event open
         * Triggering this event will load the URL given in the href or data-modal-href attribute in this modal
         * @param {string} event
         * @param {Element} element
         */
        open(event, element) {
            let url;

            if (element.attr('href')) {
                url = element.attr('href');
            } else if (element.data('href')) {
                url = element.data('href');
            }

            if (url) {
                this.load(url);
            }
        },
    };

    /**
     * @param {Object} optionsParam see configuration parameters of this class
     */
    constructor(optionsParam) {
        super();

        this.isPaused = false;

        this.options = {
            ...this.defaultOptions,
            ...optionsParam,
        };

        if (typeof this.options.loaderMessages === 'object') {
            this.options.loaderMessages = {
                ...this.defaultOptions.loaderMessages,
                ...this.options.loaderMessages,
            };
        } else {
            this.options.loaderMessages = {
                ...this.defaultOptions.loaderMessages,
                ...this.options.loaderMessages,
                _default: this.options.loaderMessages,
            };
        }

        this.loaderTemplate = _.template(this.options.loaderTemplate);

        this.modalContainer = $('<div></div>');
        this._initBootstrapModal(this.modalContainer);

        this.on(this.options.on);

        this.modalContainer.on('hide', () => {
            this.trigger('hide');
        });

        this.modalContainer.on('hidden', () => {
            this.modalContainer.detach();
            this.trigger('hidden');
        });

        this.modalContainer.on('shown', () => {
            this.trigger('shown');
        });

        if (this.options.captureKeyboard) {
            this.modalContainer.on('keydown keyup', event => event.stopPropagation());
        }
    }

    /**
     * Stop propagation of keydown and keyup events
     * @private
     */
    _suppressKeyEventPropagation() {
        this.modalElement.on('keyup keydown', (event) => {
            if (event.keyCode === 27) {
                event.preventDefault();
            }
            event.stopPropagation();
        });
    }

    /**
     * @private
     */
    _setFocusOnClick() {
        this.modalElement.on('click mouseup', (event) => {
            const target = $(event.currentTarget);
            const activeElement = $(document.activeElement);

            if (activeElement.closest(target).length === 0) {
                target.trigger('focus');
            }
        });
    }

    /**
     * @private
     */
    _catchModalLinks() {
        this.modalElement.on('click', '[data-target="modal"]', (event) => {
            event.preventDefault();
            this.load($(event.currentTarget).attr('href'));
        });
    }

    /**
     * Initialize Bootstrap modal on an element
     *
     * @param {jQuery} element
     * @private
     */
    _initBootstrapModal(element) {
        element.modal({
            keyboard: this.options.keyboard,
            backdrop: false,
            show: false,
        });
    }

    /**
     * Injects the Modal into its parent element and shows it.
     * Header and body will be parsed by underscores _.template() method.
     *
     * @param {Object} [parameters] Parameters to pass to _.template() for header and body.
     */
    show(parameters) {
        const parent = $(this.options.parent);

        // Inject the modalElement into the container
        this._resetContainer();

        this._suppressKeyEventPropagation();
        this._setFocusOnClick();

        if (!this.options.url) {
            this._setContentByOptions(parameters);
        } else {
            this._setContentByOptionsUrl();
        }

        this.trigger('beforeShow');
        parent.append(this.modalContainer);

        this.modalContainer.modal('show');
    }

    /**
     * Hides the element and removes it from the DOM.
     */
    hide() {
        this.trigger('beforeHide');
        this.modalContainer.modal('hide');
    }

    /**
     * Set this.isPaused to the value given in {paused}
     *
     * @param {boolean} paused
     * @private
     */
    _setPaused(paused) {
        this.isPaused = paused;
    }

    /**
     *
     * @return {boolean|*}
     */
    getPaused() {
        return this.isPaused;
    }

    /**
     * Hides the modal but doesn't remove it from DOM.
     * Triggers the resume event with two properties.
     */
    pause() {
        this.modalContainer.css('display', 'none');

        this.trigger('ofcs.modal.paused', {
            event: {
                ns: 'ofcs.modal',
                name: 'paused',
            },
            target: this,
        });

        this._setPaused(true);
    }

    /**
     * Displays our modal.
     * Triggers the resume event with two properties.
     */
    resume() {
        this.modalContainer.css('display', 'block');

        this.trigger('ofcs.modal.resume', {
            event: {
                ns: 'ofcs.modal',
                name: 'resume',
            },
            target: this,
        });

        this._setPaused(false);
    }

    /**
     * Reload the modal using the response of the given URL
     *
     * @param {string} url
     */
    load(url) {
        this.showLoaderByMessageKey('_default');

        $.ajax(url, {
            dataType: 'text',
        }).done((data) => {
            this._setNewModalElement(data);
            this.hideLoader();
            this.triggerEvent('ready');
        });
    }

    /**
     * @param {Object} [options]
     * @param {Object} [options.additionalData]
     * @param {string} [options.url]
     * @param {string} [options.loaderMessageKey]
     */
    async submit(options = {}) {
        const form = this._findForm();
        if (form[0]) {
            const {
                additionalData = {},
                url = form.attr('action'),
                loaderMessageKey = '_saving',
            } = options;

            try {
                const formData = new FormData(form[0]);
                Object.entries(additionalData).forEach(([key, value]) => formData.set(key, value));

                this.showLoaderByMessageKey(loaderMessageKey);
                const result = await axios.post(url, formData, {
                    onUploadProgress: ({ loaded, total }) => {
                        this.updateLoaderProgress(Math.round((loaded * 100) / total));
                    },
                });

                if (typeof result.data === 'string') {
                    this._setNewModalElement(result.data);
                }

                this.triggerEvent('ready');

                return result.data;
            } finally {
                this.hideLoader();
            }
        }

        return null;
    }

    _resetContainer() {
        this.modalElement = $(this.options.template);
        this.modalContainer.html('');
        this.modalContainer.append(this.modalElement);
    }

    /**
     * Set the content of the modalElement according to the
     * set header, footer and buttons in the options.
     *
     * @param {Object} [parameters] Parameters to pass to _.template() for header and body.
     * @private
     */
    _setContentByOptions(parameters = {}) {
        const header = $(this.options.selectors.header, this.modalElement);
        const body = $(this.options.selectors.body, this.modalElement);
        const footer = $(this.options.selectors.buttons, this.modalElement);

        this.catchPropagatedEvents(this.modalElement);

        if (header.length > 0) {
            header.html(_.template(this.options.header)(parameters));
        }
        if (body.length > 0) {
            body.html(_.template(this.options.body)(parameters));
        }

        if (footer.length > 0) {
            this.options.buttons.forEach(({ label = 'OK', icon, type, event }) => {
                const buttonElement = $('<a class="btn"></a>');

                if (type) buttonElement.addClass(`btn-${type}`);
                if (event) buttonElement.data('event', event);
                buttonElement.html(icon ? `<i class="icon-${icon}"></i> ${label}` : label);

                this._initButtonEvents(buttonElement);

                footer.append(buttonElement);
            });
        }

        this._initForms();
        this.modalElement.attr('tabindex', '-1');
        this.modalElement.trigger('focus');
        this._catchModalLinks();
        this._setButtonFocus();
    }

    /**
     * Set the content of the modalElement according to the
     * by injecting the content loaded via the configured url.
     *
     * @private
     */
    _setContentByOptionsUrl() {
        // Since this is only called on initial loading: Remove empty elements from modal
        this.modalElement.html('');

        this.load(this.options.url);
    }

    /**
     * Sets the HTML content of the modal and parses buttons for data attributes
     *
     * @param {string} modalHtml Valid jQuery selector
     * @private
     */
    _setNewModalElement(modalHtml) {
        this.modalContainer.html(modalHtml);
        const newModalElement = this.modalContainer.children(':first');

        this.modalElement = newModalElement;

        newModalElement.addClass('modal');

        this.catchPropagatedEvents(newModalElement);

        newModalElement
            .find(`${this.options.selectors.buttons} .btn`)
            .each((key, buttonElement) => {
                this._initButtonEvents($(buttonElement));
            });

        if (newModalElement.data('event')) {
            this._triggerEvents(newModalElement.data('event'), newModalElement);
        }

        this._initForms();
        this.modalElement.attr('tabindex', '-1');
        this.modalElement.trigger('focus');
        this._suppressKeyEventPropagation();
        this._setFocusOnClick();
        this._catchModalLinks();
        this._setButtonFocus();
    }

    /**
     * Returns the modal element
     * @return {jQuery}
     */
    getElement() {
        return this.modalElement;
    }

    /**
     * Catch hide, hidden, show and shown events of child elements and stop their propagation
     *
     * @param {jQuery} element
     */
    catchPropagatedEvents(element) {
        element.on('hide hidden show shown', (event) => {
            if (event.target !== element) {
                event.stopPropagation();
            }
        });
    }

    /**
     * Show loader above current modal content
     * @param {string} message
     */
    showLoader(message = '') {
        if (this.modalElement) {
            if (this.loader) {
                this.hideLoader();
            }

            this.loader = $(this.loaderTemplate({ message, percent: null })).addClass('loading');
            this.loader.data('message', message);
            this.modalElement.append(this.loader);
            this.modalElement.addClass('modal-loading');
        }
    }

    showLoaderByMessageKey(key) {
        this.showLoader(this.options.loaderMessages[key] ?? this.options.loaderMessages._default);
    }

    updateLoaderProgress(percent) {
        if (this.loader) {
            const options = {
                message: this.loader.data('message'),
                percent,
            };

            const newLoader = $(this.loaderTemplate(options))
                .addClass('loading')
                .data('message', options.message);

            this.loader.replaceWith(newLoader);
            this.loader = newLoader;
        }
    }

    /**
     * Remove the AJAX loader from this modal
     *
     * @private
     */
    hideLoader() {
        if (this.loader) {
            this.modalElement.removeClass('modal-loading');
            this.loader.remove();
            delete this.loader;
        }
    }

    /**
     * Add click event to button and have it trigger its own events
     *
     * @param {jQuery} buttonElement
     * @private
     */
    _initButtonEvents(buttonElement) {
        if (buttonElement.data('event')) {
            buttonElement.on('click keyup', (event) => {
                event.preventDefault();

                if (event.type === 'keyup' && event.keyCode !== 13) {
                    return; // Return on anything but enter
                }

                if (!buttonElement.hasClass('disabled') && !buttonElement.is(':disabled')) {
                    this._triggerEvents(buttonElement.data('event'), buttonElement, event);
                }
            });
        }
    }

    _setButtonFocus() {
        const allPrioritizedButtons = $([
            ...this.modalContainer.find('.btn-primary:not(.disabled)').toArray(),
            ...this.modalContainer.find('.btn:not(.disabled)').toArray(),
        ]);

        allPrioritizedButtons.filter(':not([tabindex])').attr('tabindex', '1');
        requestAnimationFrame(() => allPrioritizedButtons.first().trigger('focus'));
    }

    /**
     * Trigger the given event on this modal
     *
     * @param {string} event
     */
    triggerEvent(event) {
        this._triggerEvents(event, this.modalElement, event);
    }

    /**
     * Trigger all given events
     *
     * @param {string} events Event names, space separated
     * @param {jQuery} element Element that triggered the events (like a button or a modal itself)
     * @param {Event} originalEvent
     * @private
     */
    _triggerEvents(events, element, originalEvent) {
        const eventNames = events?.split(' ') ?? [];

        // eslint-disable-next-line no-restricted-syntax
        for (const eventName of eventNames) {
            const event = $.Event(originalEvent, { type: eventName });
            this.trigger(eventName, event, element, this);

            if (!event.isDefaultPrevented() && this.defaultEvents[eventName]) {
                this.defaultEvents[eventName].apply(this, [event, element]);
            }

            if (event.isImmediatePropagationStopped()) {
                break;
            }
        }
    }

    /**
     * Initialize form elements inside of this modal
     *
     * @private
     */
    _initForms() {
        const form = this._findForm();

        if (form) {
            form.on('keydown', 'input, select', (event) => {
                const target = $(event.currentTarget);

                const submitButtons = target
                    .closest('.modal')
                    .find('.modal-footer .btn')
                    .filter('[data-event*=" submit"], [data-event^="submit"]')
                    .not('.disabled');

                const hasSubmitButtons = submitButtons.length > 0;

                if (event.which === 13 && !event.isDefaultPrevented() && hasSubmitButtons) {
                    this._triggerEvents('submit', this.modalElement, event);
                    event.preventDefault();
                }
            });
        }
    }

    /**
     * Return the first form element that was found
     * @return {jQuery}
     * @private
     */
    _findForm() {
        return $(`${this.options.selectors.body} form`, this.modalElement).first();
    }
}

namespace('Ofcs.Modal');
Ofcs.Modal.Modal = Modal;
export default Modal;
